import { Components, Theme, ThemeOptions } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Extend MUI Table components with custom variants
declare module '@mui/material/TableCell' {
  interface TableCellPropsVariantOverrides {
    header: true;
    headerCheckbox: true;
    headerAction: true;
    data: true;
    checkbox: true;
    action: true;
    empty: true;
    loading: true;
  }
}

const getTableTheme = (theme: Theme): ThemeOptions => {
  const tableContainerTheme: Components<Theme>['MuiTableContainer'] = {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.M3.sys.light.surface,
        borderRadius: theme.unit(2),
        border: `1px solid ${theme.palette.M3.sys.light.outline}`,
        overflow: 'auto',
      },
    },
  };

  const tablePaginationTheme: Components<Theme>['MuiTablePagination'] = {
    defaultProps: {
      SelectProps: {
        IconComponent: ArrowDropDownIcon, // Use filled triangle chevron
      },
    },
    styleOverrides: {
      root: {
        height: 'max-content',
        maxHeight: 'max-content',
        minHeight: theme.unit(11),
        flexShrink: 0,
      },
      toolbar: {
        // Override MUI's default toolbar min-height (52px)
        minHeight: `${theme.unit(11)} !important`,
        flexShrink: 0,
        height: 'max-content',
        maxHeight: 'max-content',
      },
      select: {
        // Add gap between number and dropdown icon
        paddingRight: `${theme.unit(9)} !important`, // Increase padding to create gap
      },
      selectIcon: {
        // Position icon with gap from the number
        right: theme.unit(2), // Position icon away from edge
      },
    },
  };

  const tableCellTheme: Components<Theme>['MuiTableCell'] = {
    styleOverrides: {
      root: ({ ownerState }) => ({
        // Default cell styles
        fontSize: '14px',
        padding: theme.unit(2, 4),

        // Header cell variants
        ...(ownerState.variant === 'header' && {
          backgroundColor: theme.palette.M3.sys.light['background 2'],
          fontWeight: 500,
          color: theme.palette.M3.sys.light['on-surface'],
          padding: theme.unit(4),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          borderBottomColor: theme.palette.M3.sys.light['outline 2'],
        }),

        ...(ownerState.variant === 'headerCheckbox' && {
          backgroundColor: theme.palette.M3.sys.light['background 2'],
          fontWeight: 500,
          color: theme.palette.M3.sys.light['on-surface'],
          textAlign: 'center',
          padding: theme.unit(2, 4),
          borderBottomColor: theme.palette.M3.sys.light['outline 2'],
        }),

        ...(ownerState.variant === 'headerAction' && {
          backgroundColor: theme.palette.M3.sys.light['background 2'],
          fontWeight: 500,
          color: theme.palette.M3.sys.light['on-surface'],
          textAlign: 'center',
          padding: theme.unit(2, 4),
          borderBottomColor: theme.palette.M3.sys.light['outline 2'],
        }),

        // Data cell variants
        ...(ownerState.variant === 'data' && {
          fontSize: '14px',
          padding: theme.unit(2, 4),
          color: theme.palette.M3.sys.light['on-surface-variant'],
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }),

        ...(ownerState.variant === 'checkbox' && {
          fontSize: '14px',
          textAlign: 'center',
          padding: theme.unit(2, 4),
        }),

        ...(ownerState.variant === 'action' && {
          padding: theme.unit(2, 4),
          fontSize: '14px',
          textAlign: 'center',
        }),

        // State cells
        ...(ownerState.variant === 'empty' && {
          padding: theme.unit(4),
          textAlign: 'center',
          color: theme.palette.M3.sys.light['on-surface-variant'],
        }),

        ...(ownerState.variant === 'loading' && {
          textAlign: 'center',
          padding: theme.unit(4),
        }),
      }),
    },
  };

  return {
    components: {
      MuiTableContainer: tableContainerTheme,
      MuiTableCell: tableCellTheme,
      MuiTablePagination: tablePaginationTheme,
    },
  };
};

export default getTableTheme;
